// src/components/VideoAttendance/videoAttendanceApi.js
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

async function getAuthToken() {
  const { tokens } = await fetchAuthSession();
  const idToken = tokens?.idToken;
  if (!idToken) return null;
  const currentTime = Math.floor(Date.now() / 1000);
  if (idToken.payload?.exp != null && idToken.payload.exp <= currentTime) return null;
  return idToken.toString();
}

async function requestJson(endpoint, { method = 'GET', body = null, signal } = {}) {
  const token = await getAuthToken();
  if (!token) {
    const err = new Error('Authentication token missing/expired.');
    err.status = 401;
    throw err;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    loggingService
      .logError('api_error_video_attendance', `Network error calling ${endpoint}`, e, {
        endpoint,
        method,
      })
      .catch(() => {});
    throw e;
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => '');

  if (!res.ok) {
    const err = new Error(
      (payload && typeof payload === 'object' && (payload.message || payload.error)) ||
        `HTTP ${res.status} ${res.statusText}`
    );
    err.status = res.status;
    err.statusText = res.statusText;
    err.endpoint = endpoint;
    err.responseData = payload;

    loggingService
      .logError('api_error_video_attendance', `API error calling ${endpoint}`, err, {
        endpoint,
        method,
        status: res.status,
        statusText: res.statusText,
        responseData: payload,
      })
      .catch(() => {});
    throw err;
  }

  return payload;
}

export const videoAttendanceApi = {
  getCameras: async ({ site_id, signal } = {}) => {
    const siteId = String(site_id || '').trim();
    if (!siteId) return [];
    const qs = new URLSearchParams({ site_id: siteId });
    const data = await requestJson(`/video/cameras?${qs.toString()}`, { signal });
    return Array.isArray(data) ? data : Array.isArray(data?.cameras) ? data.cameras : data?.items || [];
  },

  createCamera: async ({ camera, signal } = {}) => {
    const body = camera && typeof camera === 'object' ? camera : {};
    return await requestJson('/video/cameras', { method: 'POST', body, signal });
  },

  updateCamera: async ({ camera_id, camera, signal } = {}) => {
    const cameraId = String(camera_id || '').trim();
    if (!cameraId) throw new Error('camera_id is required');
    const body = camera && typeof camera === 'object' ? camera : {};
    return await requestJson(`/video/cameras/${encodeURIComponent(cameraId)}`, {
      method: 'PUT',
      body,
      signal,
    });
  },

  deleteCamera: async ({ camera_id, signal } = {}) => {
    const cameraId = String(camera_id || '').trim();
    if (!cameraId) throw new Error('camera_id is required');
    return await requestJson(`/video/cameras/${encodeURIComponent(cameraId)}`, {
      method: 'DELETE',
      body: {},
      signal,
    });
  },

  heartbeatCamera: async ({ payload, signal } = {}) => {
    const body = payload && typeof payload === 'object' ? payload : {};
    return await requestJson('/video/cameras/heartbeat', { method: 'POST', body, signal });
  },

  getFrameUrl: async ({ camera_id, signal } = {}) => {
    const cameraId = String(camera_id || '').trim();
    if (!cameraId) return { url: '' };
    const qs = new URLSearchParams({ camera_id: cameraId });
    const data = await requestJson(`/video/frame-url?${qs.toString()}`, { signal });
    const url = typeof data?.url === 'string' ? data.url : '';
    return { url, raw: data };
  },

  getZoneConfig: async ({ camera_id, signal } = {}) => {
    const cameraId = String(camera_id || '').trim();
    if (!cameraId) return { zones: [] };
    const data = await requestJson(`/video/cameras/${encodeURIComponent(cameraId)}/zone-config`, { signal });
    const zones = Array.isArray(data?.zones) ? data.zones : Array.isArray(data) ? data : [];
    return { zones, raw: data };
  },

  putZoneConfig: async ({ camera_id, zoneConfig, signal } = {}) => {
    const cameraId = String(camera_id || '').trim();
    if (!cameraId) throw new Error('camera_id is required');
    const body = zoneConfig && typeof zoneConfig === 'object' ? zoneConfig : { zones: [] };
    return await requestJson(`/video/cameras/${encodeURIComponent(cameraId)}/zone-config`, {
      method: 'PUT',
      body,
      signal,
    });
  },

  getEvents: async ({ site_id, date, camera_id, limit = 50, event_type, unregistered_type, signal } = {}) => {
    const qs = new URLSearchParams();
    if (site_id) qs.set('site_id', String(site_id));
    if (date) qs.set('date', String(date));
    if (camera_id) qs.set('camera_id', String(camera_id));
    if (limit != null) qs.set('limit', String(limit));
    if (event_type) qs.set('event_type', String(event_type));
    if (unregistered_type) qs.set('unregistered_type', String(unregistered_type));
    const data = await requestJson(`/video/events?${qs.toString()}`, { signal });
    return Array.isArray(data) ? data : Array.isArray(data?.events) ? data.events : data?.items || [];
  },

  getSafetyViolations: async ({ site_id, date, camera_id, status = 'open', signal } = {}) => {
    const qs = new URLSearchParams();
    if (site_id) qs.set('site_id', String(site_id));
    if (date) qs.set('date', String(date));
    if (camera_id) qs.set('camera_id', String(camera_id));
    if (status) qs.set('status', String(status));
    const data = await requestJson(`/safety/violations?${qs.toString()}`, { signal });
    return Array.isArray(data) ? data : Array.isArray(data?.violations) ? data.violations : data?.items || [];
  },

  resolveSafetyViolation: async ({ sv_id, signal } = {}) => {
    const id = String(sv_id || '').trim();
    if (!id) throw new Error('sv_id is required');
    return await requestJson(`/safety/violations/${encodeURIComponent(id)}/resolve`, {
      method: 'PUT',
      body: {},
      signal,
    });
  },

  classifyUnregistered: async ({ payload, signal } = {}) => {
    const body = payload && typeof payload === 'object' ? payload : {};
    return await requestJson('/video/unregistered/classify', { method: 'POST', body, signal });
  },

  getBiometrics: async ({ signal } = {}) => {
    return await requestJson('/biometrics', { signal });
  },

  presignBiometrics: async ({ payload, signal } = {}) => {
    const body = payload && typeof payload === 'object' ? payload : {};
    return await requestJson('/biometrics/presign', { method: 'POST', body, signal });
  },

  enrollBiometrics: async ({ payload, signal } = {}) => {
    const body = payload && typeof payload === 'object' ? payload : {};
    return await requestJson('/biometrics/enroll', { method: 'POST', body, signal });
  },

  deleteBiometric: async ({ person_id, signal } = {}) => {
    const id = String(person_id || '').trim();
    if (!id) throw new Error('person_id is required');
    return await requestJson(`/biometrics/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
      signal,
    });
  },
};

